/**
 * Supplier side of a supplier <-> customer thread: the Messages window on the
 * Customers page.
 *
 * CR-8605: moved out of pages/Customers.tsx and onto the shared
 * SupplierMessageThread, so a failed load or a refused send says why. Also
 * opened by the bell a customer's message raises (`/customers?messages=<id>`).
 *
 * CR-8599: a thread belongs to one supplier site, and the API only lets the
 * site the user is acting as read or write it. In group view the page lists
 * customers supplied by other held sites too; for those this window offers a
 * switch to a site that supplies the customer instead of a thread that would
 * refuse every message.
 */

import { useState } from 'react';
import { SupplierSiteRef } from '../../lib/api';
import { useView } from '../../contexts/ViewContext';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import SupplierMessageThread from './SupplierMessageThread';
import { Loader2, MessageSquare } from 'lucide-react';

interface CustomerChatDialogProps {
  /** The site the user is acting as — the only one the API lets read or write. */
  supplierId: number;
  customerId: number;
  customerName: string;
  /** CR-8599: the sites in view that supply this customer; absent = not known, assume the acting site. */
  supplierSites?: SupplierSiteRef[];
  /** CR-8599: the page is loading a new scope, so the acting site is about to change. */
  refreshing?: boolean;
  onClose: () => void;
}

interface SwitchSupplierSiteNoticeProps {
  customerName: string;
  sites: SupplierSiteRef[];
}

/** Styled like the supplier-detail chat's switch-site notice (CR-8605); one button per supplying site. */
function SwitchSupplierSiteNotice({ customerName, sites }: SwitchSupplierSiteNoticeProps) {
  const { selectSite } = useView();
  const [switchingTo, setSwitchingTo] = useState<number | null>(null);

  const switchTo = async (site: SupplierSiteRef) => {
    setSwitchingTo(site.id);
    try {
      await selectSite(site.id, site.name);
    } finally {
      setSwitchingTo(null);
    }
  };

  const names = sites.map((site) => site.name).join(' or ');
  return (
    <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-sm" data-testid="switch-supplier-site-notice">
      <p>
        Messages with <span className="font-medium">{customerName}</span> are sent from{' '}
        <span className="font-medium">{names}</span>.{' '}
        {sites.length > 1
          ? 'Switch to one of those sites to read and reply.'
          : 'Switch to that site to read and reply.'}
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {sites.map((site) => (
          <Button
            key={site.id}
            size="sm"
            variant="outline"
            disabled={switchingTo !== null}
            onClick={() => switchTo(site)}
          >
            {switchingTo === site.id && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Switch to {site.name}
          </Button>
        ))}
      </div>
    </div>
  );
}

/**
 * The Messages window for one customer, as the site the user is acting as.
 *
 * @param props - See {@link CustomerChatDialogProps}
 * @returns The dialog: the thread, a switch-site notice, or a spinner while a switch loads
 */
export default function CustomerChatDialog({
  supplierId, customerId, customerName, supplierSites, refreshing = false, onClose,
}: CustomerChatDialogProps) {
  const { selectedSiteId } = useView();
  const actingSiteSupplies = !supplierSites || supplierSites.some((site) => site.id === supplierId);
  // A site the user is already viewing cannot be switched to; if that leaves
  // nothing, the thread is shown and the API's refusal explains itself.
  const switchSites = (supplierSites || []).filter((site) => site.id !== selectedSiteId);

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg flex flex-col max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary" />
            Messages — {customerName}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Conversation with {customerName}
          </DialogDescription>
        </DialogHeader>
        {refreshing ? (
          // Never open a thread as the site the user is switching away from.
          <div className="flex items-center justify-center py-12" data-testid="chat-scope-loading">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading conversation" />
          </div>
        ) : !actingSiteSupplies && switchSites.length > 0 ? (
          <SwitchSupplierSiteNotice customerName={customerName} sites={switchSites} />
        ) : (
          <SupplierMessageThread
            supplierId={supplierId}
            customerId={customerId}
            listClassName="min-h-[250px]"
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
